import React, { useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import ConnectDotsBackground from '../components/ui/ConnectDotsBackground';
import SectionHeading from '../components/ui/SectionHeading';

const FractionalCMO = () => {
  useEffect(() => {
    document.title = "Fractional CMO Services for B2B Companies | Julie Adams";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fractional CMO for B2B companies ($10M-$500M). Escape the $80K Tool Trap™ and build strategic marketing that drives ROI. 16+ years experience. Book a call.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Fractional CMO for B2B companies ($10M-$500M). Escape the $80K Tool Trap™ and build strategic marketing that drives ROI. 16+ years experience. Book a call.';
      document.head.appendChild(meta);
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-white dark:bg-brand-darker text-brand-dark dark:text-white pt-24 overflow-hidden">

      <div className="relative z-10 space-y-32 pb-32">

        {/* HERO SECTION */}
        <section className="relative pt-16 md:pt-24 text-center w-full">
          <div className="absolute inset-0 z-[-1] pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '48px 48px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="absolute inset-0 z-[-2] pointer-events-none">
            <ConnectDotsBackground />
          </div>

          <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-8 leading-tight text-brand-dark dark:text-white">
              <span className="text-brand-yellow">$80K in Marketing Tools.</span> <br />
              <span className="text-brand-dark dark:text-white">Where's the ROI?</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-700 dark:text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              I help B2B companies ($10M-$500M revenue) escape the Tool Trap™ and build strategic marketing functions that drive measurable revenue—without the $250K+ cost of a full-time CMO.
            </p>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-16">
            <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900/50 px-5 py-3 rounded-lg border border-slate-200 dark:border-slate-800">
              <SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0" />
              <span className="font-semibold text-sm md:text-base">16+ Years Building Marketing Operations</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900/50 px-5 py-3 rounded-lg border border-slate-200 dark:border-slate-800">
              <SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0" />
              <span className="font-semibold text-sm md:text-base">$2B+ in Client Revenue Impact</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900/50 px-5 py-3 rounded-lg border border-slate-200 dark:border-slate-800">
              <SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0" />
              <span className="font-semibold text-sm md:text-base">Client Case Study: 40% Martech Efficiency Lift</span>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <a
              href="https://calendar.app.google/xG1VHH4LHxW9Ppq87"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-brand-red text-white px-8 py-4 rounded-md text-lg font-bold hover:scale-105 hover:shadow-[0_0_15px_rgba(64,224,208,0.5)] transition-all duration-300 w-full md:w-auto"
            >
              Book Your Discovery Call
            </a>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400 font-medium">
              30-minute strategic assessment. No pitch, just clarity.
            </p>
          </div>
          </div>
        </section>

        {/* DOES THIS SOUND FAMILIAR? (TOOL TRAP™) */}
        <section className="px-4 sm:px-6 max-w-[900px] mx-auto bg-white dark:bg-brand-dark/50 py-16 md:py-24 rounded-2xl relative overflow-hidden">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black mb-4 inline-block relative">
              Does This Sound Familiar?
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-brand-yellow rounded-full"></div>
            </h2>
          </div>

          <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-12 leading-relaxed">
            You're spending $50K-$200K per year on marketing technology. CRM, Automations, Six analytics platforms. A content tool for every channel. Your team swears they need every single one.
          </p>
          <p className="text-lg md:text-xl font-bold mb-8">
            But here's what's actually happening:
          </p>

          <div className="bg-slate-50 dark:bg-slate-900 border-l-4 border-brand-red p-8 mb-12 rounded-r-lg shadow-sm">
            <h3 className="text-2xl font-bold mb-6 text-brand-dark dark:text-white">The $80K Tool Trap™</h3>
            <ul className="space-y-5">
              <li className="flex gap-4 items-start">
                <SafeIcon name="AlertCircle" className="w-6 h-6 text-brand-cyan shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">Your marketing team spends 60% of their time managing tools instead of driving strategy</span>
              </li>
              <li className="flex gap-4 items-start">
                <SafeIcon name="AlertCircle" className="w-6 h-6 text-brand-cyan shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">Data lives in 12 different places, and nobody can answer "what's working?"</span>
              </li>
              <li className="flex gap-4 items-start">
                <SafeIcon name="AlertCircle" className="w-6 h-6 text-brand-cyan shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">Your CMO left six months ago, and the team is drowning in tactical execution</span>
              </li>
              <li className="flex gap-4 items-start">
                <SafeIcon name="AlertCircle" className="w-6 h-6 text-brand-cyan shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">Revenue goals keep moving, but marketing can't prove their impact</span>
              </li>
              <li className="flex gap-4 items-start">
                <SafeIcon name="AlertCircle" className="w-6 h-6 text-brand-cyan shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-slate-300">Your CEO is asking why marketing spend increased 40% while pipeline stayed flat</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold mb-6 text-brand-yellow">You're Not Alone</h3>
          <p className="text-lg text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
            B2B companies with 50-500 employees are stuck in what I call the Tool Trap™: the false belief that more marketing technology equals better marketing results.
          </p>
          <p className="text-lg text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
            The reality? You don't have a tool problem. <strong className="text-brand-red">You have a strategy problem.</strong>
          </p>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
            And without a senior marketing leader who understands both business strategy and marketing operations, that tool stack becomes an expensive anchor instead of an engine.
          </p>
        </section>

        {/* WHAT IS A FRACTIONAL CMO */}
        <section className="bg-slate-50 dark:bg-slate-900/30 py-24 border-y border-slate-200 dark:border-slate-800">
          <div className="px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-16">
               <h2 className="text-3xl md:text-5xl font-black mb-4 inline-block relative">
                What Is a Fractional CMO?
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-brand-yellow rounded-full"></div>
              </h2>
              <p className="text-xl mt-6 text-brand-red font-semibold">(And Why Your B2B Company Needs One)</p>
            </div>

            <div className="max-w-4xl mx-auto text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-20 text-center leading-relaxed">
              <p className="mb-8">
                A fractional CMO is a senior marketing executive who works with your company part-time (typically 2-4 days per week) to provide strategic leadership, build operational frameworks, and drive measurable revenue impact—without the $250K-$400K annual cost of a full-time Chief Marketing Officer.
              </p>
              <h3 className="text-2xl font-bold text-brand-dark dark:text-white mb-6">How a Fractional CMO Fixes Marketing Tool Chaos</h3>
              <p>
                Unlike marketing consultants who deliver recommendations and leave, or full-time CMOs who take 3-6 months to onboard, a fractional CMO provides immediate strategic leadership with hands-on implementation.
              </p>
            </div>

            <h3 className="text-3xl font-black text-center mb-12">My Three-Phase Methodology</h3>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Phase 1 */}
              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-lg hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-brand-cyan/10 rounded-full flex items-center justify-center text-brand-cyan font-black text-xl shrink-0">1</div>
                  <h4 className="text-xl font-bold">AUDIT <span className="block text-sm text-brand-yellow font-normal mt-1">(Weeks 1-3)</span></h4>
                </div>
                <h5 className="font-bold mb-4 text-slate-900 dark:text-white">Marketing Operations Assessment</h5>
                <ul className="space-y-3 mb-8 text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Complete martech stack audit (tools, cost, utilization, redundancy)</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Team capability mapping and workflow analysis</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Data architecture review across CRM, analytics, and reporting systems</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Revenue attribution gap analysis</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Strategic framework alignment assessment</li>
                </ul>
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border-l-2 border-brand-yellow">
                  <span className="font-bold block mb-1">Deliverable:</span>
                  <span className="text-sm">Your Tool Trap™ Diagnosis—a prioritized roadmap showing exactly where you're bleeding budget and how to stop it.</span>
                </div>
              </div>

              {/* Phase 2 */}
              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-lg hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-brand-cyan/10 rounded-full flex items-center justify-center text-brand-cyan font-black text-xl shrink-0">2</div>
                  <h4 className="text-xl font-bold">STRATEGY <span className="block text-sm text-brand-yellow font-normal mt-1">(Weeks 4-8)</span></h4>
                </div>
                <h5 className="font-bold mb-4 text-slate-900 dark:text-white">Strategic Architecture Development</h5>
                <ul className="space-y-3 mb-8 text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Marketing function redesign aligned to business objectives</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Team structure optimization and role clarity</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Martech consolidation strategy (eliminate 30-40% redundant tools)</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Data unification and reporting framework</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> 90-day revenue-focused marketing plan</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Team leadership and capability building</li>
                </ul>
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border-l-2 border-brand-yellow">
                  <span className="font-bold block mb-1">Deliverable:</span>
                  <span className="text-sm">Your Strategic Marketing Blueprint—the operational foundation your team needs to execute with clarity.</span>
                </div>
              </div>

              {/* Phase 3 */}
              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-lg hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-brand-cyan/10 rounded-full flex items-center justify-center text-brand-cyan font-black text-xl shrink-0">3</div>
                  <h4 className="text-xl font-bold">IMPLEMENTATION <span className="block text-sm text-brand-yellow font-normal mt-1">(Ongoing)</span></h4>
                </div>
                <h5 className="font-bold mb-4 text-slate-900 dark:text-white">Framework Execution & Leadership</h5>
                <ul className="space-y-3 mb-8 text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Hands-on implementation of strategic frameworks</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Team leadership, coaching, and accountability systems</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Cross-functional alignment (sales, product, customer success)</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Performance monitoring and optimization</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Board-ready reporting and revenue attribution</li>
                </ul>
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border-l-2 border-brand-yellow">
                  <span className="font-bold block mb-1">Deliverable:</span>
                  <span className="text-sm">A marketing function that operates like a revenue engine, not a cost center.</span>
                </div>
              </div>
            </div>

            {/* What Makes This Approach Different */}
            <div className="mt-20 bg-brand-dark text-white rounded-2xl p-8 md:p-12 border border-slate-800 shadow-xl">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-3xl font-black mb-8 text-center">What Makes This Approach Different</h3>
                <h4 className="text-xl font-bold mb-6 text-brand-yellow">I'm a MarkOps Orchestrator, Not Just a Strategist</h4>
                <p className="text-slate-300 mb-8 text-lg">
                  Most fractional CMOs come from brand or demand gen backgrounds. I built my career at the intersection of marketing strategy, technology operations, and data architecture—which means I don't just tell you what to do, I architect the systems that make execution possible.
                </p>
                <ul className="space-y-4 text-slate-300">
                  <li className="flex gap-3">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan shrink-0" />
                    <span><strong className="text-white">I speak both CMO and CTO:</strong> Bridging the gap between marketing vision and technical reality</span>
                  </li>
                  <li className="flex gap-3">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan shrink-0" />
                    <span><strong className="text-white">I diagnose the Tool Trap™:</strong> My proprietary assessment identifies exactly where your martech spend is wasted</span>
                  </li>
                  <li className="flex gap-3">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan shrink-0" />
                    <span><strong className="text-white">I build, not just advise:</strong> You get strategic frameworks + implementation + team leadership</span>
                  </li>
                  <li className="flex gap-3">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan shrink-0" />
                    <span><strong className="text-white">I focus on revenue, not vanity metrics:</strong> Every initiative ties directly to pipeline and closed deals</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* PRICING */}
        <section className="relative py-24 w-full">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="relative z-10 px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-6">How Much Does a Fractional CMO Cost?</h2>
            <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 max-w-4xl mx-auto leading-relaxed">
              Fractional CMO services typically range from $10,000 to $25,000 per month depending on time commitment and scope—a fraction of the $250K-$400K annual cost of a full-time CMO, without recruitment fees, benefits, or onboarding delays.
            </p>
            <h3 className="text-2xl font-bold mt-12 mb-4">Three Service Tiers</h3>
            <p className="text-slate-500 uppercase tracking-widest text-sm font-bold mb-12">(Minimum 3-Month Engagement)</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start mb-20">
            {/* Tier 1 */}
            <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">STRATEGIC GUIDANCE</h4>
              <div className="text-4xl font-black text-brand-dark dark:text-white mb-2">$10,000<span className="text-lg font-normal text-slate-500">/month</span></div>
              <div className="text-sm font-bold text-slate-500 mb-6">2 days per week</div>

              <div className="mb-8">
                <p className="text-sm italic text-brand-yellow font-medium">
                  Ideal for: B2B companies with an existing marketing manager who needs strategic oversight and operational frameworks.
                </p>
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
                <h5 className="font-bold text-sm uppercase tracking-wider text-slate-900 dark:text-white mb-4">What's Included:</h5>
                <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Marketing operations audit and Tool Trap™ diagnosis</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Strategic planning and framework development</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Team coaching and capability building</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Monthly leadership meetings with CEO/executive team</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> 90-day marketing roadmap aligned to revenue goals</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Tool stack optimization recommendations</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Quarterly board-ready reporting</li>
                </ul>
              </div>
            </div>

            {/* Tier 2 - Most Popular */}
            <div className="bg-white dark:bg-brand-dark border-2 border-brand-red rounded-xl shadow-2xl relative transform md:-translate-y-4 hover:-translate-y-5 transition-all duration-300">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-yellow text-brand-dark px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider shadow-md whitespace-nowrap">
                ⭐ Most Popular
              </div>
              <div className="p-8">
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">STRATEGIC + LEADERSHIP</h4>
                <div className="text-4xl font-black text-brand-dark dark:text-white mb-2">$15,000<span className="text-lg font-normal text-slate-500">/month</span></div>
                <div className="text-sm font-bold text-slate-500 mb-6">3 days per week</div>

                <div className="mb-8">
                  <p className="text-sm italic text-brand-yellow font-medium">
                    Ideal for: B2B companies rebuilding after CMO departure or scaling from $10M to $50M+ revenue.
                  </p>
                </div>

                <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
                  <h5 className="font-bold text-sm uppercase tracking-wider text-slate-900 dark:text-white mb-4">Everything in Strategic Guidance, plus:</h5>
                  <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Hands-on team leadership and management</li>
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Cross-functional alignment (sales, product, customer success)</li>
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Martech consolidation and implementation oversight</li>
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Data unification and reporting system buildout</li>
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Weekly team standup and accountability systems</li>
                    <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Direct ownership of marketing OKRs and revenue targets</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">FULL FRACTIONAL CMO</h4>
              <div className="text-4xl font-black text-brand-dark dark:text-white mb-2">$25,000<span className="text-lg font-normal text-slate-500">/month</span></div>
              <div className="text-sm font-bold text-slate-500 mb-6">4 days per week</div>

              <div className="mb-8">
                <p className="text-sm italic text-brand-yellow font-medium">
                  Ideal for: B2B companies ($50M-$500M revenue) needing full executive leadership during transition, rapid growth, or transformation.
                </p>
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
                <h5 className="font-bold text-sm uppercase tracking-wider text-slate-900 dark:text-white mb-4">Everything in Strategic + Leadership, plus:</h5>
                <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Senior executive presence in all leadership meetings</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Board presentation and investor communication support</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> M&A marketing integration and due diligence</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Enterprise martech vendor negotiation and optimization</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Complete marketing P&L ownership</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Executive-level crisis management and strategic pivots</li>
                </ul>
              </div>
            </div>
          </div>

          {/* What's Included Overall */}
          <div className="max-w-4xl mx-auto bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-8 md:p-12 border border-slate-200 dark:border-slate-800 mb-16">
            <h3 className="text-2xl font-black mb-8 text-center">What's Included in Every Engagement</h3>
            <div className="grid md:grid-cols-2 gap-y-4 gap-x-8">
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Marketing operations audit within first 30 days</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Tool Trap™ diagnosis and cost optimization roadmap</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Strategic framework development and implementation</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Team leadership and capability building</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Revenue-focused metrics and board-ready reporting</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Slack/Teams access for real-time collaboration</span></div>
              <div className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> <span>Monthly executive briefings</span></div>
            </div>

            <div className="mt-8 p-6 bg-brand-dark text-white rounded-lg border-l-4 border-brand-red">
              <div className="flex gap-4 items-start">
                <SafeIcon name="Box" className="w-8 h-8 text-brand-yellow shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg mb-2 text-brand-yellow">Access to SEVEN HATS® Strategic Operations Platform</h4>
                  <p className="text-sm text-slate-300">My proprietary command center for building and running high-performing marketing functions. No more scattered tools, fragmented workflows, or strategies that live in slide decks. SEVEN HATS® is where strategy becomes operational reality.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h4 className="text-xl font-bold mb-4">Minimum Engagement: 3 Months</h4>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-8">
              Strategic marketing transformation takes time. All fractional CMO engagements require a minimum 3-month commitment to ensure meaningful impact and measurable results.
            </p>
            <a
              href="https://calendar.app.google/xG1VHH4LHxW9Ppq87"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-brand-red text-white px-8 py-4 rounded-md text-lg font-bold hover:scale-105 hover:shadow-[0_0_15px_rgba(64,224,208,0.5)] transition-all duration-300 w-full md:w-auto"
            >
              Book Your Discovery Call
            </a>
          </div>
          </div>
        </section>

        {/* FEATURED CASE STUDY & RESULTS */}
        <section className="relative bg-slate-50 dark:bg-slate-900/30 py-24 border-y border-slate-200 dark:border-slate-800 w-full">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="relative z-10 px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-black mb-4 inline-block relative">
                Fractional CMO Results
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-brand-yellow rounded-full"></div>
              </h2>
              <p className="text-xl mt-6 text-brand-dark dark:text-white font-semibold">Real Companies, Real Impact</p>
            </div>

            <div className="max-w-5xl mx-auto bg-white dark:bg-brand-dark border-l-8 border-brand-cyan rounded-xl shadow-xl overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-8">
                  <SafeIcon name="TrendingUp" className="w-8 h-8 text-brand-cyan" />
                  <h3 className="text-2xl font-black uppercase tracking-wider">Featured Case Study: $30M Manufacturing Company</h3>
                </div>

                <div className="grid md:grid-cols-2 gap-12">
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 text-lg">Company Profile:</h4>
                    <p className="text-slate-600 dark:text-slate-400 mb-8">
                      $2B industrial manufacturer, 60+ years in business, fragmented complex martech stack across multiple divisions.
                    </p>

                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 text-lg">The Challenge:</h4>
                    <p className="text-slate-600 dark:text-slate-400 mb-8">
                      Without a CMO seat, this $30M B2B manufacturing company's marketing team was managing 23 separate tools with significant redundancy, disconnected data systems, and no clear strategy for unifying marketing operations across multiple business units.
                    </p>

                    <h4 className="font-bold text-slate-900 dark:text-white mb-4 text-lg">The Fractional CMO Solution:</h4>
                    <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                      <li className="flex gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-brand-cyan shrink-0 mt-1" /> Conducted comprehensive martech audit across all divisions (completed in 21 days)</li>
                      <li className="flex gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-brand-cyan shrink-0 mt-1" /> Identified 40% tool redundancy and $80K in annual wasted spend</li>
                      <li className="flex gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-brand-cyan shrink-0 mt-1" /> Developed unified data architecture connecting CRM, analytics, and reporting systems</li>
                      <li className="flex gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-brand-cyan shrink-0 mt-1" /> Implemented strategic framework for cross-divisional marketing operations</li>
                      <li className="flex gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-brand-cyan shrink-0 mt-1" /> Built accountability systems, process maps, and team leadership structure</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-8 flex flex-col justify-center border border-slate-100 dark:border-slate-800">
                    <h4 className="font-black text-2xl text-center mb-8 uppercase tracking-widest text-slate-900 dark:text-white">Measurable Results</h4>

                    <div className="space-y-8">
                      <div className="text-center">
                        <div className="text-4xl md:text-5xl font-black text-brand-red mb-2">40%</div>
                        <div className="font-bold text-slate-900 dark:text-white">Immediate reduction in redundant martech spend</div>
                        <div className="text-sm text-brand-yellow font-bold mt-1">($80K annual savings)</div>
                      </div>

                      <div className="text-center">
                        <div className="text-3xl font-black text-brand-cyan mb-2">Unified Data</div>
                        <div className="font-bold text-slate-900 dark:text-white">Architecture across 4 business divisions</div>
                      </div>

                      <div className="text-center">
                        <div className="text-3xl font-black text-brand-cyan mb-2">90 Days</div>
                        <div className="font-bold text-slate-900 dark:text-white">To implement strategic framework</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="px-4 sm:px-6 max-w-7xl mx-auto py-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4 inline-block relative">
              What Clients Say
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-brand-yellow rounded-full"></div>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl relative shadow-sm hover:shadow-md transition-shadow">
              <SafeIcon name="MessageSquare" className="w-12 h-12 text-slate-100 dark:text-slate-800 absolute top-6 right-6 -z-10" />
              <div className="text-brand-yellow text-4xl font-serif leading-none mb-4">"</div>
              <p className="text-slate-700 dark:text-slate-300 font-medium mb-6 relative z-10">
                Julie doesn't just identify problems—she builds the systems that solve them.
              </p>
              <div className="text-sm font-bold text-slate-900 dark:text-white">— Hazely Lopez, Client Partner</div>
            </div>

            <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl relative shadow-sm hover:shadow-md transition-shadow">
              <SafeIcon name="MessageSquare" className="w-12 h-12 text-slate-100 dark:text-slate-800 absolute top-6 right-6 -z-10" />
              <div className="text-brand-yellow text-4xl font-serif leading-none mb-4">"</div>
              <p className="text-slate-700 dark:text-slate-300 font-medium mb-6 relative z-10">
                As a marketing leader who speaks both strategy and technology, Julie bridged many gaps between our siloed marketing and sales departments - highlighting areas of improvement that made a big impact on how we run our organization.
              </p>
              <div className="text-sm font-bold text-slate-900 dark:text-white">— Kenji T., Technology Executive</div>
            </div>

            <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl relative shadow-sm hover:shadow-md transition-shadow">
              <SafeIcon name="MessageSquare" className="w-12 h-12 text-slate-100 dark:text-slate-800 absolute top-6 right-6 -z-10" />
              <div className="text-brand-yellow text-4xl font-serif leading-none mb-4">"</div>
              <p className="text-slate-700 dark:text-slate-300 font-medium mb-6 relative z-10">
                The Tool Trap™ diagnosis alone saved us six figures in the first year. Her proprietery strategic framework transformed how our marketing team operates. Hire her.
              </p>
              <div className="text-sm font-bold text-slate-900 dark:text-white">— B2B Manufacturing CEO, $150M revenue</div>
            </div>
          </div>
        </section>

        {/* CREDIBILITY MARKERS */}
        <section className="bg-brand-darker py-16 border-y border-slate-800">
          <div className="px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 text-center md:text-left">
              <div className="flex flex-col items-center md:items-start">
                <SafeIcon name="Briefcase" className="w-8 h-8 text-brand-cyan mb-4" />
                <h4 className="font-bold text-white mb-2 text-lg">16+ Years Experience</h4>
                <p className="text-slate-400 text-sm">Building JAC Marketing from startup to strategic marketing operations firm</p>
              </div>
              <div className="flex flex-col items-center md:items-start">
                <SafeIcon name="DollarSign" className="w-8 h-8 text-brand-cyan mb-4" />
                <h4 className="font-bold text-white mb-2 text-lg">$2B+ Client Revenue</h4>
                <p className="text-slate-400 text-sm">Impact across B2B manufacturers, executives, and high-growth companies</p>
              </div>
              <div className="flex flex-col items-center md:items-start">
                <SafeIcon name="Globe" className="w-8 h-8 text-brand-cyan mb-4" />
                <h4 className="font-bold text-white mb-2 text-lg">Diverse Portfolio</h4>
                <p className="text-slate-400 text-sm">From Grammy-Winning Artists & Political Figures, to Industrial Manufacturers</p>
              </div>
              <div className="flex flex-col items-center md:items-start">
                <SafeIcon name="BookOpen" className="w-8 h-8 text-brand-cyan mb-4" />
                <h4 className="font-bold text-white mb-2 text-lg">Industry Leader</h4>
                <p className="text-slate-400 text-sm">Author of "Brand Like A Girl" & Founder of SEVEN HATS® Marketing Operations Platform</p>
              </div>
            </div>
          </div>
        </section>

        {/* WHY WORK WITH JULIE ADAMS */}
        <section className="relative py-24 w-full">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="relative z-10 px-4 sm:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4 inline-block relative">
              Why Work With Me?
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-brand-yellow rounded-full"></div>
            </h2>
            <p className="text-xl mt-6 text-slate-700 dark:text-slate-300 font-bold max-w-3xl mx-auto">
              I am the Rare Executive Who Speaks Both Marketing and Technology
            </p>
          </div>

          <div className="space-y-12 mb-20 text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              Most CMOs come from brand, demand gen, or creative backgrounds. I built my career at the intersection of marketing strategy, technology operations, and data architecture—which is exactly why I can diagnose and solve the Tool Trap™ that's costing you six figures.
            </p>

            <div>
              <h4 className="text-xl font-bold text-brand-dark dark:text-white mb-3 flex items-center gap-3">
                <SafeIcon name="Award" className="w-6 h-6 text-brand-cyan" /> 16+ Years Building Marketing Operations
              </h4>
              <p className="pl-9">
                I've spent nearly two decades building JAC Marketing, working with companies from $10M startups to $2B+ manufacturers. I've seen every version of marketing tool chaos, and I know exactly how to fix it.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-brand-dark dark:text-white mb-3 flex items-center gap-3">
                <SafeIcon name="Cpu" className="w-6 h-6 text-brand-cyan" /> I Bridge the IT/Marketing Divide
              </h4>
              <p className="pl-9">
                Your CTO and CMO should speak the same language. I'm fluent in both—which means I can translate marketing needs into technical requirements, negotiate with vendors who respect operational expertise, and build systems that actually work.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-brand-dark dark:text-white mb-3 flex items-center gap-3">
                <SafeIcon name="Book" className="w-6 h-6 text-brand-cyan" /> Author & Thought Leader
              </h4>
              <p className="pl-9">
                As the author of "Brand Like A Girl" and founder of the SEVEN HATS® marketing operations platform, I don't just implement best practices—I define them.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-brand-dark dark:text-white mb-3 flex items-center gap-3">
                <SafeIcon name="Target" className="w-6 h-6 text-brand-cyan" /> Proven Methodology
              </h4>
              <p className="pl-9">
                The Tool Trap™ diagnosis isn't theory—it's a proprietary framework I've used with dozens of B2B companies to eliminate 30-40% of wasted martech spend while improving marketing performance.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold text-brand-dark dark:text-white mb-3 flex items-center gap-3">
                <SafeIcon name="PieChart" className="w-6 h-6 text-brand-cyan" /> Executive-Level Experience Without Executive-Level Cost
              </h4>
              <p className="pl-9">
                You get the strategic insight and operational expertise of a $250K-$400K CMO at a fraction of the cost, with immediate impact and no 6-month onboarding period.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900 p-8 md:p-12 rounded-2xl border border-slate-200 dark:border-slate-800">
            <h3 className="text-3xl font-black text-center mb-12 text-brand-yellow">Who This Is For (And Who It's Not For)</h3>

            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-cyan" /> Ideal For:
                </h4>
                <ul className="space-y-4 text-slate-700 dark:text-slate-300">
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> B2B companies with $10M-$500M in annual revenue</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Organizations with 50-500 employees</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Companies spending $50K-$200K/year on martech with unclear ROI</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Teams drowning in tool chaos and tactical execution</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Businesses recovering from CMO departure or scaling rapidly</li>
                  <li className="flex gap-3"><SafeIcon name="Check" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> CEOs/COOs who know marketing should drive revenue but can't prove it does</li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <SafeIcon name="XCircle" className="w-6 h-6 text-brand-red" /> NOT A Fit If:
                </h4>
                <ul className="space-y-4 text-slate-700 dark:text-slate-300">
                  <li className="flex gap-3"><SafeIcon name="X" className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> You're looking for a marketing executor (not a strategic leader)</li>
                  <li className="flex gap-3"><SafeIcon name="X" className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> You want someone to "fix the website" or "run ads" without strategic foundation</li>
                  <li className="flex gap-3"><SafeIcon name="X" className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> You're not willing to make hard decisions about tool consolidation</li>
                  <li className="flex gap-3"><SafeIcon name="X" className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> Your timeline expectation is 30 days (transformation takes 90+ days)</li>
                  <li className="flex gap-3"><SafeIcon name="X" className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" /> You're below $10M revenue (consider SEVEN HATS® platform instead)</li>
                </ul>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* THE SEVEN HATS ADVANTAGE */}
        <section className="relative bg-brand-dark/5 dark:bg-brand-cyan/5 py-24 border-y border-brand-dark/10 dark:border-brand-cyan/10 w-full">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="relative z-10 px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-16 max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-black mb-8 text-brand-dark dark:text-white">The SEVEN HATS® Advantage</h2>
              <p className="text-xl text-slate-700 dark:text-slate-300 leading-relaxed font-medium mb-6">
                Most fractional CMOs bring experience and advice. I bring both along with a proprietary platform.
              </p>
              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                SEVEN HATS® is the strategic operations system I built over 16 years—it's not another tool to manage, it's the command center where we build your marketing function.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl shadow-sm">
                <div className="flex gap-4 items-start mb-4">
                  <SafeIcon name="Zap" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                  <h4 className="text-xl font-bold text-brand-yellow">Strategy Meets Execution</h4>
                </div>
                <p className="text-slate-700 dark:text-slate-300 pl-10">
                  No more strategies that die in PowerPoint. SEVEN HATS® translates executive vision into operational frameworks your team can execute immediately. Every engagement begins with strategy deployed directly into working systems.
                </p>
              </div>

              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl shadow-sm">
                <div className="flex gap-4 items-start mb-4">
                  <SafeIcon name="Maximize" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                  <h4 className="text-xl font-bold text-brand-yellow">Built for Scale Without Complexity</h4>
                </div>
                <p className="text-slate-700 dark:text-slate-300 pl-10">
                  Whether you're launching your first marketing hire or managing a distributed team, SEVEN HATS® adapts to your growth stage. The same frameworks that work for a $5M startup scale seamlessly to a $50M enterprise.
                </p>
              </div>

              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl shadow-sm">
                <div className="flex gap-4 items-start mb-4">
                  <SafeIcon name="Layers" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                  <h4 className="text-xl font-bold text-brand-yellow">One System, Seven Functions</h4>
                </div>
                <p className="text-slate-700 dark:text-slate-300 pl-10">
                  Brand positioning, content operations, demand generation, customer lifecycle, sales enablement, analytics infrastructure, and team development—all unified in a single strategic framework. Your marketing function operates from one source of truth.
                </p>
              </div>

              <div className="bg-white dark:bg-brand-dark border border-slate-200 dark:border-slate-800 p-8 rounded-xl shadow-sm">
                <div className="flex gap-4 items-start mb-4">
                  <SafeIcon name="Shield" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                  <h4 className="text-xl font-bold text-brand-yellow">Proprietary, Not Off-the-Shelf</h4>
                </div>
                <p className="text-slate-700 dark:text-slate-300 pl-10">
                  This isn't repackaged marketing automation. SEVEN HATS® represents 16 years of building marketing organizations from scratch, documenting what actually works, and eliminating what doesn't. You get the distilled playbook of dozens of successful engagements.
                </p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto bg-brand-dark text-white rounded-xl p-8 md:p-12 text-center border-l-4 border-brand-red shadow-xl">
              <h4 className="text-2xl font-bold mb-6">Operational Continuity Beyond My Engagement</h4>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                When our fractional engagement concludes, your team inherits a complete operating system, not just advice. They know exactly how decisions get made, campaigns get launched, and performance gets measured—because it's all systematized in SEVEN HATS®.
              </p>
              <p className="text-xl font-bold italic text-brand-yellow mb-4">
                "Other fractional CMOs leave you with strategic recommendations. I leave you with a functioning marketing operation that your team can run independently."
              </p>
              <p className="text-xl font-bold italic text-brand-yellow">
                That's the SEVEN HATS® advantage.
              </p>
            </div>

            <p className="text-center mt-12 text-sm uppercase tracking-widest font-bold text-slate-500 mb-2">
              Every fractional CMO engagement includes full access to SEVEN HATS® frameworks, templates, and operational playbooks.
            </p>
            <p className="text-center text-sm uppercase tracking-widest font-bold text-slate-500">
              It's not an add-on—it's how I work.
            </p>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="relative py-24 text-center w-full">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)' }}></div>
          <div className="relative z-10 px-4 sm:px-6 max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black mb-8 text-brand-dark dark:text-white leading-tight">
            Ready to Escape the <br /> <span className="text-brand-yellow">$80K Tool Trap™?</span>
          </h2>
          <h3 className="text-2xl font-bold mb-12 text-brand-red">Book Your 30-Minute Discovery Call</h3>

          <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 md:p-12 rounded-2xl mb-12 text-left shadow-sm">
            <h4 className="text-xl font-bold mb-8 uppercase tracking-widest text-center">Here's what we'll cover in your strategic assessment:</h4>
            <ul className="space-y-6 text-lg text-slate-700 dark:text-slate-300">
              <li className="flex gap-4">
                <SafeIcon name="Check" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                <span><strong className="text-brand-dark dark:text-white">Tool Trap™ Quick Diagnosis</strong> — I'll ask 10 questions that reveal exactly where you're bleeding budget</span>
              </li>
              <li className="flex gap-4">
                <SafeIcon name="Check" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                <span><strong className="text-brand-dark dark:text-white">Marketing Operations Assessment</strong> — We'll identify your biggest gaps in strategy, team, and systems</span>
              </li>
              <li className="flex gap-4">
                <SafeIcon name="Check" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                <span><strong className="text-brand-dark dark:text-white">Fractional CMO Fit Evaluation</strong> — Honest conversation about whether this approach makes sense for your business</span>
              </li>
              <li className="flex gap-4">
                <SafeIcon name="Check" className="w-6 h-6 text-brand-cyan shrink-0 mt-1" />
                <span><strong className="text-brand-dark dark:text-white">Next Steps</strong> — If we're aligned, I'll outline a 90-day roadmap for immediate impact</span>
              </li>
            </ul>
          </div>

          <p className="text-xl font-bold italic text-slate-500 mb-10">No pitch. No pressure. Just clarity.</p>

          <a
            href="https://calendar.app.google/xG1VHH4LHxW9Ppq87"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-brand-red text-white px-10 py-5 rounded-md text-xl font-bold hover:scale-105 hover:shadow-[0_0_20px_rgba(64,224,208,0.5)] transition-all duration-300 w-full md:w-auto shadow-lg"
          >
            Book Your Discovery Call
          </a>

          <div className="mt-20 max-w-2xl mx-auto text-left">
            <h4 className="text-2xl font-bold mb-8 text-center text-brand-dark dark:text-white">What Happens After You Book:</h4>
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan font-bold shrink-0">1</div>
                <p className="text-slate-700 dark:text-slate-300 mt-1">You'll receive a pre-call questionnaire (5 minutes) so we can maximize our time together</p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan font-bold shrink-0">2</div>
                <p className="text-slate-700 dark:text-slate-300 mt-1">We'll meet for 60 minutes via Zoom for your strategic assessment</p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan font-bold shrink-0">3</div>
                <p className="text-slate-700 dark:text-slate-300 mt-1">Within 24 hours from our call, you'll receive a follow-up with key insights and recommended next steps</p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan font-bold shrink-0">4</div>
                <p className="text-slate-700 dark:text-slate-300 mt-1">If we're a fit, we'll discuss engagement timeline and onboarding (most clients start within 2-3 weeks)</p>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* SECTION: Still Have Questions? */}
        <section className="py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-brand-dark dark:text-white">Still Have Questions?</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-lg">
            <p className="text-slate-700 dark:text-slate-300">
              Email:{' '}
              <a
                href="mailto:julie@sevenhats.com"
                className="font-bold text-brand-dark dark:text-white hover:text-brand-red dark:hover:text-brand-red transition-colors"
              >
                julie@sevenhats.com
              </a>
            </p>
            <div className="hidden md:block w-1 h-1 bg-slate-300 dark:bg-slate-700 rounded-full"></div>
            <p className="text-slate-700 dark:text-slate-300">
              LinkedIn:{' '}
              <a
                href="https://www.linkedin.com/in/julieadamsmsdm"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-brand-dark dark:text-white hover:text-brand-red dark:hover:text-brand-red transition-colors"
              >
                /in/julieadams
              </a>
            </p>
          </div>
        </section>

      </div>

      {/* FOOTER */}
      <footer className="bg-brand-dark text-white py-16 px-4 sm:px-6 md:px-8 text-center relative z-20">
        <div className="max-w-6xl mx-auto flex flex-col items-center">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 mb-12 text-sm md:text-base text-slate-300 font-medium">
            <span>16+ Years Proven Results</span>
            <span className="hidden md:inline text-brand-cyan/50">•</span>
            <span>$2B+ Client Portfolio</span>
            <span className="hidden md:inline text-brand-cyan/50">•</span>
            <span>Author of Brand Like A Girl</span>
            <span className="hidden md:inline text-brand-cyan/50">•</span>
            <span>Founder of SEVEN HATS®</span>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-slate-400">
            <a href="/privacy" className="hover:text-brand-cyan transition-colors">Privacy Policy</a>
            <span className="text-slate-600">|</span>
            <a href="/terms" className="hover:text-brand-cyan transition-colors">Terms of Service</a>
            <span className="text-slate-600">|</span>
            <a href="/contact" className="hover:text-brand-cyan transition-colors">Contact</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default FractionalCMO;
